'use client';

import { useCallback } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { updateChannelMembership } from '../functions/members/updateChannelMembership';

/**
 * Action-only hook that posts notification-setting toggles for the
 * current user's channel membership. Live read-state is via
 * `useMyChannelMembership` — keep the two split so the read can be
 * subscribed and the write stays a one-shot mutation.
 */
export function useChannelNotificationSettings({
  channelId,
}: {
  channelId: string | null | undefined;
}) {
  const { creatorId } = useCommunityContext();
  const currentUserId = useCurrentUserId();

  const setMuted = useCallback(
    async (muted: boolean) => {
      if (!creatorId || !currentUserId || !channelId) return;
      await updateChannelMembership({
        creatorId,
        channelId,
        userId: currentUserId,
        muted,
      });
    },
    [creatorId, currentUserId, channelId],
  );

  const setSubscribedToMentions = useCallback(
    async (subscribedToMentions: boolean) => {
      if (!creatorId || !currentUserId || !channelId) return;
      await updateChannelMembership({
        creatorId,
        channelId,
        userId: currentUserId,
        subscribedToMentions,
      });
    },
    [creatorId, currentUserId, channelId],
  );

  const setSubscribedToUpdatesOfCreator = useCallback(
    async (subscribedToUpdatesOfCreator: boolean) => {
      if (!creatorId || !currentUserId || !channelId) return;
      await updateChannelMembership({
        creatorId,
        channelId,
        userId: currentUserId,
        subscribedToUpdatesOfCreator,
      });
    },
    [creatorId, currentUserId, channelId],
  );

  return { setMuted, setSubscribedToMentions, setSubscribedToUpdatesOfCreator };
}
